/**
 * The purpose of this module is to get every links that targets some specific domains on the current page
 * and to add to them the tracking parameters.
 */

import domready from "domready";
import queryString from "query-string";
import SEOUtils from "../../utils/SEOUtils/SEOUtils";

// Expose SEOUtils globally if we are in a browser context, to be able to use it everywhere (mainly in sq.js)
if (window) {
  window["SEOUtils"] = SEOUtils;
}

function addTrackingParametersToUrls() {
  const outboundLinks = drupalSettings.sq?.tracking?.outboundLinks;

  // We need to have drupalSettings.sq.tracking.outboundLinks available
  // to be able to test if a link has to be modified.
  if (!outboundLinks?.length) {
    return;
  }

  const regexps = outboundLinks.map((regex: string) => {
    return new RegExp(regex, "i");
  });

  // Find every link and add tracking URL parameters on given URLs.
  document.querySelectorAll("a, iframe").forEach((element: HTMLElement) => {
    const nodeName = element.nodeName.toLowerCase();
    const isIframe = nodeName === "iframe";
    const attribute = isIframe ? "src" : "href";
    const url = element.getAttribute(attribute);
    let parameter = null;

    for (let i = 0; i < regexps.length && !parameter; i++) {
      if (regexps[i].test(url)) {
        parameter = url;
      }
    }

    if (parameter) {
      element.setAttribute(attribute, SEOUtils.addUrlParameters(parameter));
      element.dataset.trackingEnabled = "1";

      // Sometimes, browser ignore src changes.
      // We replace the iframe with its clone to restart loading.
      if (isIframe) {
        element.parentElement.insertBefore(element.cloneNode(), element);
        element.remove();
      }
    }
  });
}

function addTrackingParametersToCookies() {
  const cookies = drupalSettings.sq?.tracking?.cookie;

  // We need to have drupalSettings.sq.tracking.cookie available to be able to test if a link has to be modified.
  if (!cookies) {
    return;
  }

  const parameterRegex = /^[A-z\d\-_]+$/;
  const parameters = queryString.parse(window.location.search);

  Object.keys(cookies).forEach(key => {
    if (!parameters[key]) {
      return;
    }

    const directives = drupalSettings.sq.tracking.cookie[key];
    const parameterValue = parameters[key] as string;

    if (parameterRegex.exec(parameterValue) === null) {
      return;
    }

    const directivesArray = [];

    for (const directive in directives) {
      if (directives.hasOwnProperty(directive)) {
        // Special cases: Secure and HttpOnly has to be written without equals
        if (directive === "Secure" || directive === "HttpOnly") {
          if (directives[directive]) {
            directivesArray.push(directive);
          }

          continue;
        }
        // Max age is null: it's a session cookie, we do not add the Max-Age directive
        else if (directive === "Max-Age" && !directives[directive]) {
          continue;
        }

        directivesArray.push(`${directive}=${directives[directive]}`);
      }
    }

    // cookie-js is not used here because most of the directives are currently not supported by it.
    const cookieString = `sq.tracking.${key}=${encodeURIComponent(parameterValue)}; ${directivesArray.join("; ")}`;
    document.cookie = cookieString;
  });
}

addTrackingParametersToCookies();
domready(addTrackingParametersToUrls);
